import { Trans, useTranslation } from 'react-i18next';

import { Header } from '../../../atoms/header/header';
import { Input } from '../../../atoms/input/input';
import { Label } from '../../../atoms/label/label';
import { Text } from '../../../atoms/text/text';
import { PASSWORD_MIN_LENGTH, PASSWORD_PATTERN } from '../password-validation';

import { FormFieldProps, PasswordFieldsProps, PasswordInputProps } from './types';
import { UsernameField } from './username-field';

const PasswordInput = ({ setShowPassword, repeat, variant, ...props }: PasswordInputProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="password">{repeat ? t('auth.common.repeatPasswordLabel') : t('auth.common.passwordLabel')}</Label>
      <Input
        placeholder={t('auth.common.passwordPlaceholder')}
        suffix={
          <Text className="text-neutral-700 cursor-pointer" onClick={() => setShowPassword((s) => !s)} tag="p">
            {t('auth.common.showPassword')}
          </Text>
        }
        variant={variant}
        {...props}
      />
      {!repeat && <Text size="sm">{t('auth.common.errors.passwordFormatDescription')}</Text>}
    </div>
  );
};

const PasswordFields = ({
  setShowPassword,
  passwordInputType,
  errors,
  register,
  t,
  watchPassword,
}: PasswordFieldsProps) => (
  <>
    <PasswordInput
      setShowPassword={setShowPassword}
      type={passwordInputType}
      variant={errors.password ? 'error' : 'default'}
      {...register('password', {
        required: t('auth.common.errors.passwordRequired'),
        pattern: {
          value: PASSWORD_PATTERN,
          message: t('auth.common.errors.passwordFormatDescription'),
        },
        minLength: {
          value: PASSWORD_MIN_LENGTH,
          message: t('auth.common.errors.passwordTooShort'),
        },
      })}
    />
    <PasswordInput
      repeat
      setShowPassword={setShowPassword}
      type={passwordInputType}
      variant={errors.confirmPassword ? 'error' : 'default'}
      {...register('confirmPassword', {
        required: t('auth.common.errors.passwordConfirmationMissing'),
        validate: (value: string) => value === watchPassword || t('auth.common.errors.passwordsDoNotMatch'),
      })}
    />
  </>
);

const Heading = ({
  headerKey,
  signInUrl,
  subHeaderKey,
}: {
  readonly headerKey: string;
  readonly signInUrl?: string;
  readonly subHeaderKey?: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <Header level={'h1'}>{t(headerKey)}</Header>
      {subHeaderKey && <Text className="mt-4">{t(subHeaderKey)}</Text>}
      {signInUrl && (
        <Text>
          <Trans
            components={{
              signInLink: (
                <a className="underline" href={signInUrl}>
                  {t('auth.login.signInButtonText')}
                </a>
              ),
            }}
            i18nKey={'auth.register.alreadyHaveAccountSubHeader'}
          />
        </Text>
      )}
    </div>
  );
};

const FormFields = ({
  control,
  clearErrors,
  errors,
  headerKey,
  passwordInputType,
  register,
  renderInformation: RenderInformation,
  showRegistrationMethodToggle,
  setValue,
  setShowPassword,
  subHeaderKey,
  switchToEmailLabelKey,
  switchToMobileLabelKey,
  watchPassword,
  passwordLess,
  defaultMethod,
  setError,
  signInUrl,
  allowedEmailDomains,
}: FormFieldProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6">
      <Heading headerKey={headerKey} signInUrl={signInUrl} subHeaderKey={subHeaderKey} />
      {RenderInformation && <RenderInformation />}
      <UsernameField
        allowedEmailDomains={allowedEmailDomains}
        clearErrors={clearErrors}
        control={control}
        defaultMethod={defaultMethod}
        error={errors.username}
        register={register}
        setError={setError}
        setValue={setValue}
        showRegistrationMethodToggle={showRegistrationMethodToggle}
        switchToEmailLabelKey={switchToEmailLabelKey}
        switchToMobileLabelKey={switchToMobileLabelKey}
      />
      {!passwordLess && (
        <PasswordFields
          errors={errors}
          passwordInputType={passwordInputType}
          register={register}
          setShowPassword={setShowPassword}
          t={t}
          watchPassword={watchPassword}
        />
      )}
    </div>
  );
};

export { FormFields };
